// Reviiri import helpers. Reviiri is a Finnish hunting territory service; a club's map
// boundary is imported after signing in to a Reviiri account. Unlike the Oma Riista
// import — one public code, no login — this flow is two steps: connect an account, then
// pick one of the maps that account can see.

/** One importable map. `id` is CLUB-PREFIXED (`"{clubId}/{mapId}"`) and is passed back
    to the import endpoint verbatim; it is also the ground's importRef, which is what
    makes a re-import refresh instead of duplicate. */
export interface ReviiriMap {
  id: string
  name: string
}

export interface ReviiriClub {
  id: string
  name: string
  maps: ReviiriMap[]
}

/**
 * Result of connecting a Reviiri account. `rejected` is the user's problem (wrong email
 * or password) and gets a specific message; `error` carries the backend's stable code
 * so the UI can show it for support without depending on the domain's ApiError.
 */
export type ReviiriConnectResult =
  | { status: 'connected'; email: string }
  | { status: 'rejected' }
  | { status: 'error'; code: string | null }

/**
 * Result of listing what the connected account can see. `not-connected` covers BOTH "no
 * account is linked" and "the linked token expired" — the server clears a stale token
 * before answering, so from here they are the same situation and get the same UI: the
 * sign-in form.
 */
export type ReviiriClubsResult =
  | { status: 'ok'; clubs: ReviiriClub[] }
  | { status: 'not-connected' }
  | { status: 'error'; code: string | null }

export type ReviiriImportResult =
  | { status: 'created' }
  | { status: 'refreshed' }
  | { status: 'not-connected' }
  | { status: 'not-found' }
  | { status: 'error'; code: string | null }

/**
 * Whether the sign-in button should be enabled. Deliberately NOT an email-format check:
 * Reviiri decides what a valid account is, and a client-side pattern would silently lock
 * out accounts whose shape we guessed wrong. Empty fields are the only thing worth
 * catching before a round trip.
 */
export function canSubmitReviiriLogin(email: string, password: string): boolean {
  return email.trim().length > 0 && password.length > 0
}

/** Total importable maps across every club — used for the "no maps at all" empty state. */
export function reviiriMapCount(clubs: ReviiriClub[]): number {
  return clubs.reduce((total, club) => total + club.maps.length, 0)
}
